<template>
  <main class="hero" ref="heroSection">
    <div class="hero-content" ref="heroContent">
      <div class="hero-header">
        <motion.div
            class="profile-section"
            :initial="{ opacity: 0, y: 5 }"
            :whileInView="{ opacity: 1, y: 0 }"
            :transition="{ duration: 0.5 }"
            :inViewOptions="{ once: true }"
        >
          <img src="@/assets/ariel-podzorski-profile-picture.webp" alt="Ariel Podzorski – Laravel Entwickler in Osnabrück" class="profile-photo">
        </motion.div>
        <motion.div
            class="title-section"
            :initial="{ opacity: 0, y: 5 }"
            :whileInView="{ opacity: 1, y: 0 }"
            :transition="{ delay: 0.5, duration: 0.5 }"
            :inViewOptions="{ once: true }"
        >
          <gradient-title text="Ariel Podzorski"/>
        </motion.div>
      </div>

      <motion.div
          class="description"
          :initial="{ opacity: 0, y: 5 }"
          :whileInView="{ opacity: 1, y: 0 }"
          :transition="{ delay: 1, duration: 0.5 }"
          :inViewOptions="{ once: true }"
      >
        <p>Senior Full-Stack Entwickler. Seit 7 Jahren baue ich skalierbare Webanwendungen mit Laravel und Vue.js.</p>
        <p>Mein Fokus: Durchdachte Software-Architektur, Testbarkeit und wartbarer Code statt Quick-and-Dirty-Lösungen.</p>
      </motion.div>
      <motion.div
          class="portfolio-button"
          :initial="{ opacity: 0, y: 5 }"
          :whileInView="{ opacity: 1, y: 0 }"
          :transition="{ delay: 1.5, duration: 0.5 }"
          :inViewOptions="{ once: true }"
      >
        <a href="https://www.freelancermap.de/profil/ariel-podzorski">
          <gradient-button>
            Hire me
          </gradient-button>
        </a>
      </motion.div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { motion } from "motion-v"
import GradientTitle from "@/components/GradientTitle.vue"
import GradientButton from "@/components/GradientButton.vue"

const heroSection = ref<HTMLElement>()
const heroContent = ref<HTMLElement>()

const handleScroll = () => {
  if (!heroContent.value || !heroSection.value) return

  const scrollY = window.scrollY
  const heroHeight = heroSection.value.offsetHeight
  const scrollProgress = Math.min(scrollY / (heroHeight * 0.6), 1)

  const opacity = Math.max(1 - scrollProgress * 1.2, 0)
  const offset = Math.round(scrollProgress * 100);

  heroContent.value.style.opacity = opacity.toString()
  heroContent.value.style.transform = `translate(-50%, calc(-50% - ${offset}px))`;
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.hero {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
}

.hero-content {
  text-align: center;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  width: 100%;
  max-width: 1000px;
  padding: 0 2rem;
}

.hero-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
}

.profile-section {
  flex-shrink: 0;
}

.profile-photo {
  width: 120px;
  height: 120px;
  border-radius: 12px;
  object-fit: cover;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1), 0 5px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.profile-photo:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15), 0 8px 20px rgba(0, 0, 0, 0.1);
}

.title-section {
  text-align: left;
}

.description {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #555;
  max-width: 600px;
  margin: 0 auto;
}

.description p {
  margin-bottom: 1rem;
}

.description p:last-child {
  margin-bottom: 0;
}

.portfolio-button {
  margin-top: 1rem;
  display: inline-block;
}

@media (max-width: 768px) {
  .hero {
    padding: 1rem;
  }

  .hero-content {
    padding: 0 1rem;
  }

  .hero-header {
    flex-direction: column;
    gap: 1.5rem;
  }

  .profile-photo {
    width: 100px;
    height: 100px;
  }

  .title-section {
    text-align: center;
  }

  .description {
    font-size: 1rem;
  }
}
</style>
